import { useNavigate } from "react-router-dom";
import { Boxes, MessageCircle, Send } from "lucide-react";
import { money, openChat, slug, TELEGRAM_URL, unitPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export const BulkHint = ({ item }) => {
  const tiers = item.bulk_tiers || [];
  if (!tiers.length) return null;
  const id = slug(item.name);
  const next = tiers.find((t) => t.min > item.qty);
  const applied = unitPrice(item, item.qty) < item.price;
  return (
    <div className="mt-2 space-y-0.5 text-xs">
      {applied && <p data-testid={`bulk-applied-${id}`} className="text-amber-400">Bulk price applied: {money(unitPrice(item, item.qty))} each</p>}
      {next && <p data-testid={`bulk-nudge-${id}`} className="text-gray-400">Add {next.min - item.qty} more to pay {money(next.price)} each</p>}
    </div>
  );
};

const save = (base, p) => Math.round((1 - p / base) * 100);

const TierRow = ({ game, tier, best, onBuy, id }) => (
  <button data-testid={`bulk-tier-${id}-${tier.min}`} onClick={() => onBuy(tier.min)}
    className={`w-full grid grid-cols-[1fr_auto_auto] items-center gap-3 px-3 sm:px-4 min-h-12 py-1.5 rounded-md border text-left transition-colors ${best ? "border-amber-500/70 bg-amber-500/10" : "border-white/10 bg-[#08090C]/60 hover:border-amber-500/50"}`}>
    <span className="text-sm font-semibold whitespace-nowrap flex flex-col leading-tight">{tier.min}+ keys {best && <span className="text-[10px] uppercase tracking-wider text-amber-400">Best value</span>}</span>
    <span className="font-mono font-bold text-amber-400 whitespace-nowrap">{money(tier.price)}<span className="text-xs text-gray-500 font-normal"> /key</span></span>
    <span className="text-xs font-mono px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400 w-14 text-center">-{save(game.price, tier.price)}%</span>
  </button>
);

export const BulkDeal = ({ game }) => {
  const { add } = useCart();
  const navigate = useNavigate();
  const tiers = game.bulk_tiers || [];
  if (!tiers.length) return null;
  const id = slug(game.name);
  const buy = (qty) => { add(game, qty); navigate("/checkout"); };
  const ask = () => openChat(`Hi! I'd like a custom bulk order of ${game.name} codes. Quantity: `);
  return (
    <div data-testid={`bulk-deal-${id}`} className="rounded-lg border border-amber-500/40 bg-amber-500/[0.06] p-4 sm:p-5 space-y-4">
      <div className="flex items-start gap-3">
        <Boxes size={20} className="text-amber-400 shrink-0 mt-0.5" />
        <div>
          <p className="label-eyebrow text-amber-400">Reseller prices</p>
          <p className="text-sm text-gray-300 mt-1">The more keys you buy, the less each one costs. Tap a tier to order it. Fewer than {tiers[0].min} keys stay at {money(game.price)} each.</p>
        </div>
      </div>
      <div data-testid={`bulk-tiers-${id}`} className="space-y-2">
        {tiers.map((t, i) => <TierRow key={t.min} game={game} tier={t} best={i === tiers.length - 1} onBuy={buy} id={id} />)}
      </div>
      <p data-testid={`bulk-price-${id}`} className="text-xs text-gray-400">Example: {tiers[0].min} keys = {money(tiers[0].price * tiers[0].min)} USDT</p>
      <button data-testid={`bulk-buy-${id}`} onClick={() => buy(tiers[0].min)}
        className="w-full h-11 rounded-md bg-amber-500 text-[#08090C] font-bold hover:bg-amber-400 transition-colors">Buy {tiers[0].min} codes</button>
      <div>
        <p className="text-xs text-gray-400 mb-2">Need a custom amount? Contact us:</p>
        <div className="grid grid-cols-2 gap-3">
          <button data-testid={`bulk-contact-${id}`} onClick={ask}
            className="h-11 rounded-md border border-white/15 font-semibold inline-flex items-center justify-center gap-2 hover:border-amber-500/60 transition-colors"><MessageCircle size={16} /> Live chat</button>
          <a data-testid={`bulk-telegram-${id}`} href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer"
            className="h-11 rounded-md border border-sky-500/40 text-sky-300 font-semibold inline-flex items-center justify-center gap-2 hover:border-sky-400 transition-colors"><Send size={16} /> Telegram</a>
        </div>
      </div>
    </div>
  );
};
